import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";

import { Option, Options } from "types";
import { ReactComponent as ArrowDown } from "static/icons/arrow-down-blue.svg";
import useOnClickOutside from "hooks/useClickOutside";
import styles from "./Dropdown.module.scss";

export interface DropdownProps {
    id?: string;
    selectedOption?: Option | null;
    onChange: (option: Option) => void;
    options: Options | null;
    disabled?: boolean;
    searchable?: boolean;
    classes?: {
        root?: string;
        header?: string;
        label?: string;
        arrow?: string;
        list?: string;
        listItem?: string;
        text?: string;
        listItemActive?: string;
    };
    placeholder?: string;
    acceptInput?: boolean;
}

interface DropdownListProps extends Exclude<DropdownProps, "id"> {
    open: boolean;
    closeDropdown: () => void;
}

const NoOption = () => {
    return <li className={styles["no-options"]}>No options</li>;
};

const DropdownList = ({
    options,
    selectedOption,
    onChange,
    closeDropdown,
    classes,
    open,
}: DropdownListProps) => {
    function onClickDropdownItem(option: Option) {
        return () => {
            onChange(option);
            closeDropdown();
        };
    }
    return (
        <ul
            className={classNames(
                styles["dropdown__list"],
                {
                    [styles["dropdown__list--open"]]: open,
                },
                classes?.list,
            )}
        >
            {(!options || options.length === 0) && <NoOption />}
            {options &&
                options.length > 0 &&
                options.map(option => {
                    return (
                        <li
                            onClick={onClickDropdownItem(option)}
                            key={options.indexOf(option)}
                            className={classNames(
                                styles["dropdown__list-item"],
                                {
                                    [styles["dropdown__list-item--selected"]]:
                                        selectedOption?.value === option.value,
                                },
                                classes?.listItem,
                                selectedOption?.value === option.value && classes?.listItemActive,
                            )}
                        >
                            <span
                                className={classNames(
                                    styles["dropdown__list-item-text"],
                                    classes?.text,
                                )}
                            >
                                {option.label}
                            </span>
                        </li>
                    );
                })}
        </ul>
    );
};

const Dropdown = ({
    selectedOption,
    options,
    onChange,
    classes,
    id,
    searchable,
    placeholder = "",
    disabled,
    acceptInput,
}: DropdownProps) => {
    const [open, setOpen] = useState(false);
    const [inputText, setInputText] = useState("");
    const ref = useRef(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    function closeDropdown() {
        setOpen(false);
    }

    function onInputClick(event: React.MouseEvent<HTMLInputElement>) {
        // allow user to edit the text without closing the dropdown
        event.stopPropagation();
        setOpen(true);
    }

    function toggleDropdown() {
        if (disabled) {
            return;
        }
        setOpen(!open);
        if (searchable && !open && inputRef && inputRef.current) {
            /*
      clicking on the arrow could open the dropdown without the focusing the input, hence this is needed,
      to make sure the input is editable whenever the dropdown is opened.
      */
            inputRef.current.focus();
        }
    }

    function handleClickOutSide() {
        closeDropdown();
    }

    function handleInputTextChange(event: React.ChangeEvent<HTMLInputElement>) {
        setInputText(event.target.value);
    }

    useEffect(() => {
        if (searchable) {
            setInputText("");
        }
    }, [open]);

    useOnClickOutside(ref, handleClickOutSide);

    const memorizedOptions = React.useMemo(() => {
        const filteredOptions =
            options?.filter(option => {
                const normalizedLabel = option.label.toLowerCase();
                const normalizedValue = option.value.toLowerCase();
                const normalizedInputText = inputText.toLowerCase();
                return (
                    normalizedValue.includes(normalizedInputText) ||
                    normalizedLabel.includes(normalizedInputText)
                );
            }) || null;
        if (filteredOptions?.length === 0 && acceptInput) {
            filteredOptions.push({
                label: inputText,
                value: inputText,
            });
        }
        return filteredOptions;
    }, [options, inputText]);
    return (
        <div id={id} ref={ref} className={classNames(styles["dropdown"], classes?.root)}>
            <div
                role="presentation"
                className={classNames(styles["dropdown__header"], classes?.header)}
                onClick={toggleDropdown}
            >
                {searchable ? (
                    <input
                        ref={inputRef}
                        type="text"
                        disabled={disabled}
                        onClick={onInputClick}
                        onChange={handleInputTextChange}
                        value={inputText}
                        className={classNames(styles["dropdown__input"], classes?.label)}
                        placeholder={selectedOption?.label || placeholder}
                    />
                ) : (
                    <span
                        className={classNames(styles["dropdown__selected-label"], classes?.label)}
                    >
                        {selectedOption?.label || placeholder}
                    </span>
                )}
                <ArrowDown
                    className={classNames(
                        styles["dropdown__arrow"],
                        {
                            [styles["dropdown__arrow--open"]]: open,
                        },
                        classes?.arrow,
                    )}
                />
            </div>
            <DropdownList
                options={memorizedOptions}
                classes={classes}
                open={open}
                closeDropdown={closeDropdown}
                selectedOption={selectedOption}
                onChange={onChange}
            />
        </div>
    );
};

export default Dropdown;
